import {
  Box,
  Typography,
  Paper,
  IconButton,
  useMediaQuery,
  useTheme,
  alpha,
  CircularProgress,
} from '@mui/material';
import { ChevronLeft, ChevronRight } from '@mui/icons-material';
import { useState, useEffect, useRef, useMemo } from 'react';
import { Document, Page } from 'react-pdf';
import { pdfjs } from 'react-pdf';
import 'react-pdf/dist/Page/AnnotationLayer.css';
import 'react-pdf/dist/Page/TextLayer.css';
import useLog from 'hooks/useLog';
import { markSegments } from 'util/search';

pdfjs.GlobalWorkerOptions.workerSrc = new URL(
  'pdfjs-dist/build/pdf.worker.min.mjs',
  import.meta.url,
).toString();

interface PublishedPdfViewerProps {
  pdfUrl: string;
  mode?: 'single-page' | 'all-pages';
  /** Surface forms the search matched. Empty in browse mode. */
  highlightTerms?: string[];
}

// customTextRenderer hands its return value to the text layer as HTML, which
// makes this the one place in the feature where a string of markup is
// legitimate, and so the one place the text itself has to be escaped first.
const HTML_ESCAPES: Record<string, string> = {
  '&': '&amp;',
  '<': '&lt;',
  '>': '&gt;',
  '"': '&quot;',
  "'": '&#39;',
};

const escapeHtml = (text: string) =>
  text.replace(/[&<>"']/g, (char) => HTML_ESCAPES[char]);

const PublishedPdfViewer = ({
  pdfUrl,
  mode = 'single-page',
  highlightTerms = [],
}: PublishedPdfViewerProps) => {
  const [numPages, setNumPages] = useState<number | null>(null);
  const [pageNumber, setPageNumber] = useState(1);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const currentPdfUrl = useRef<string>(pdfUrl);
  const { logError } = useLog('PublishedPdfViewer');

  // Memoize the options object to prevent unnecessary reloads
  const documentOptions = useMemo(
    () => ({
      cMapUrl: '/cmaps/',
    }),
    [],
  );

  // pdf.js breaks a line into items at typesetter boundaries, so a two-word
  // term straddles two items more often than not and never matches whole.
  // Matching word by word costs a phrase its single mark, since it highlights
  // as two adjacent ones, and is the only way it gets marked at all.
  const pdfTerms = useMemo(
    () => [
      ...new Set(
        highlightTerms
          .flatMap((term) => term.split(/\s+/))
          .filter((word) => word !== ''),
      ),
    ],
    [highlightTerms],
  );

  // Memoized because a new identity re-renders every text layer on the page,
  // which is the expensive half of rendering a PDF.
  const customTextRenderer = useMemo(() => {
    if (pdfTerms.length === 0) {
      return undefined;
    }
    // color:transparent is not optional. The text layer is an invisible
    // overlay, hidden by TextLayer.css with '.textLayer :is(span, br) {
    // color: transparent }', and a <mark> is neither a span nor a br, so it
    // misses that rule and picks up the UA stylesheet's own 'color: MarkText'.
    // A direct declaration beats an inherited one whatever its origin, so the
    // marked glyphs turn opaque and paint a second copy of the word over the
    // canvas, in a fallback font squeezed by the span's scaleX. Only the
    // background may be visible here.
    const markStyle = [
      `background-color:${alpha(theme.palette.warning.light, 0.45)}`,
      'color:transparent',
    ].join(';');
    return ({ str }: { str: string }) =>
      markSegments(str, pdfTerms)
        .map((segment) =>
          segment.marked
            ? `<mark style="${markStyle}">${escapeHtml(segment.text)}</mark>`
            : escapeHtml(segment.text),
        )
        .join('');
  }, [pdfTerms, theme]);

  // Reset state when pdfUrl changes
  useEffect(() => {
    if (currentPdfUrl.current !== pdfUrl) {
      setNumPages(null);
      setPageNumber(1);
      setIsLoading(true);
      setError(null);
      currentPdfUrl.current = pdfUrl;
    }
  }, [pdfUrl]);

  const onDocumentLoadSuccess = ({ numPages }: { numPages: number }) => {
    setNumPages(numPages);
    setPageNumber(1);
    setIsLoading(false);
    setError(null);
  };

  const onDocumentLoadError = (error: Error) => {
    logError(error, 'onDocumentLoadError');
    setIsLoading(false);
    setError(error.message || 'Failed to load PDF document');
  };

  const previousPage = () =>
    setPageNumber((prevPageNumber) => prevPageNumber - 1);

  const nextPage = () => setPageNumber((prevPageNumber) => prevPageNumber + 1);

  const loadingStatePdf = (
    <Box
      sx={{
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'center',
        p: 4,
        gap: 2,
        minHeight: isMobile ? 400 : 600,
      }}
    >
      <CircularProgress />
      <Typography variant="body2" color="text.secondary">
        Loading PDF...
      </Typography>
    </Box>
  );

  const loadingStatePage = (
    <Box
      sx={{
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'center',
        p: 4,
        gap: 2,
        minHeight: isMobile ? 400 : 600,
      }}
    >
      <CircularProgress />
      <Typography variant="body2" color="text.secondary">
        Loading page...
      </Typography>
    </Box>
  );

  const errorDisplay = (
    <Box
      sx={{
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'center',
        p: 4,
        gap: 2,
        minHeight: isMobile ? 400 : 600,
      }}
    >
      <Typography variant="h6" color="error" textAlign="center">
        Failed to load PDF
      </Typography>
      <Typography variant="body2" color="text.secondary" textAlign="center">
        {error}
      </Typography>
    </Box>
  );

  if (error != null) {
    return errorDisplay;
  }

  if (mode === 'all-pages') {
    return (
      <Box
        sx={{
          width: '100%',
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          gap: 2,
          px: isMobile ? 1 : 2,
          pb: 4,
        }}
      >
        <Document
          key={pdfUrl}
          file={pdfUrl}
          onLoadSuccess={onDocumentLoadSuccess}
          onLoadError={onDocumentLoadError}
          loading={loadingStatePdf}
          error={errorDisplay}
          options={documentOptions}
        >
          {numPages != null &&
            !isLoading &&
            Array.from(new Array(numPages), (_el, index) => (
              <Paper
                key={`page_${index + 1}`}
                elevation={2}
                sx={{
                  display: 'block',
                  width: '100%',
                  maxWidth: isMobile ? '100%' : '800px',
                  mb: 2,
                }}
              >
                <Page
                  pageNumber={index + 1}
                  width={
                    isMobile
                      ? Math.min(window.innerWidth - 32, 600)
                      : Math.min(800, window.innerWidth - 64)
                  }
                  loading={loadingStatePage}
                  customTextRenderer={customTextRenderer}
                  error={
                    <Typography color="error" textAlign="center" p={2}>
                      Failed to load page {index + 1}
                    </Typography>
                  }
                />
              </Paper>
            ))}
        </Document>
      </Box>
    );
  }

  return (
    <Box
      sx={{
        width: '100%',
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        gap: 2,
        p: 1,
      }}
    >
      {/* Navigation Controls */}
      <Paper
        elevation={2}
        sx={{
          p: isMobile ? 1 : 2,
          display: 'flex',
          alignItems: 'center',
          gap: isMobile ? 1 : 2,
          borderRadius: 2,
        }}
      >
        <IconButton
          onClick={previousPage}
          disabled={pageNumber <= 1 || isLoading}
          color="primary"
          size={isMobile ? 'medium' : 'large'}
          sx={{
            borderRadius: 1,
          }}
        >
          <ChevronLeft />
        </IconButton>

        <Typography
          variant={isMobile ? 'body2' : 'body1'}
          sx={{
            minWidth: isMobile ? 80 : 120,
            textAlign: 'center',
            fontSize: {
              xs: 13,
              md: 15,
            },
          }}
        >
          Page {pageNumber || (numPages != null ? 1 : '--')} of{' '}
          {numPages ?? '--'}
        </Typography>

        <IconButton
          onClick={nextPage}
          disabled={pageNumber >= (numPages ?? 0) || isLoading}
          color="primary"
          size={isMobile ? 'medium' : 'large'}
          sx={{
            borderRadius: 1,
          }}
        >
          <ChevronRight />
        </IconButton>
      </Paper>

      {/* PDF Document Container */}
      <Box
        sx={{
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'flex-start',
          overflow: 'auto',
          bgcolor: alpha(theme.palette.primary.main, 0.25),
          borderRadius: 1,
          p: 1,
        }}
      >
        <Paper
          elevation={2}
          sx={{
            display: 'inline-block',
            maxWidth: '100%',
          }}
        >
          <Document
            key={pdfUrl}
            file={pdfUrl}
            onLoadSuccess={onDocumentLoadSuccess}
            onLoadError={onDocumentLoadError}
            loading={loadingStatePdf}
            error={errorDisplay}
            options={documentOptions}
          >
            <Page
              pageNumber={pageNumber}
              width={
                isMobile ? Math.min(window.innerWidth - 32, 600) : undefined
              }
              loading={loadingStatePage}
              customTextRenderer={customTextRenderer}
              error={
                <Typography color="error" textAlign="center" p={2}>
                  Failed to load page {pageNumber}
                </Typography>
              }
            />
          </Document>
        </Paper>
      </Box>
    </Box>
  );
};

export default PublishedPdfViewer;
